import React, { useMemo } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import StatWithIndicator from '../../stories/StatWithIndicator/StatWithIndicator'
import {
  Data,
  Metric,
  getDataKeys,
  useData,
  useMetrics,
} from '../../widgets/api'
import { useSuspenseQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import classes from './PerformanceComponents.module.css'
import { Typography } from '../../common/typography'
import { Fmt, useFmtColor } from '../../widgets/format'
import { useWidgetContext } from '../../widgets/providers'
import PeriodSelect from '../../stories/PeriodPicker/PeriodSelect'

type KPIData = {
  name: string
  actual?: number
  target?: number
  percent?: number
  teams_achieved?: number
  players_achieved?: number
  metric: Metric
}

function usePerformanceData(): KPIData[] {
  const kpis = useMetrics().data.filter((m) => m.is_kpi)
  const context = useWidgetContext()

  const metrics: string[] = []
  const components: string[] = []
  const statistics: string[] = []

  ;[
    ['target', 'value'],
    ['actual', 'value'],
    ['percent', 'value'],
    ['percent', 'achieved'],
    ['percent', 'achieved_teams'],
  ].forEach(([component, statistic]) => {
    kpis.forEach((kpi) => {
      metrics.push(kpi.key)
      components.push(component)
      statistics.push(statistic)
    })
  })

  const { data: dataKeys } = useSuspenseQuery({
    queryKey: ['data-keys'],
    queryFn: getDataKeys,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  // Use context values or fallback to defaults
  const period_type = context.period_type || 'total'
  const period = context.period || '2025-01-01'
  const subject_type = context.subject_type || 'overall'
  const subject_ids = context.subject ? [context.subject] : [0]

  const { data } = useData(
    {
      period_type: period_type,
      period_range: false,
      periods: [period],
      subject_type,
      subject_ids,
      metrics,
      components,
      statistics,
      competition_id: context.competition_id || 0,
    },
    'PerformancePulse'
  )

  const kpi_data: KPIData[] = useMemo(() => {
    const result: {
      [kpi_key: string]: {
        actual?: number
        target?: number
        percent?: number
        achieved?: number
        achieved_team?: number
      }
    } = {}

    // Map data points to the MatchStats structure
    data.forEach((dataPoint: Data) => {
      const kpi = dataPoint.metric

      result[kpi.key] = result[kpi.key] || {}

      const comp = dataPoint.component
      const stat = dataPoint.statistic
      if (comp === 'target') {
        result[kpi.key].target = dataPoint.value
      } else if (comp === 'actual') {
        result[kpi.key].actual = dataPoint.value
      } else if (comp === 'percent') {
        if (stat === 'value') {
          result[kpi.key].percent = dataPoint.value
        } else if (stat === 'achieved') {
          result[kpi.key].achieved = dataPoint.value
        } else if (stat === 'achieved_teams') {
          result[kpi.key].achieved_team = dataPoint.value
        }
      }
    })

    return kpis.map((kpi) => ({
      name: kpi.name,
      metric: kpis.find((k) => k.key === kpi.key)!,
      actual: result[kpi.key]?.actual,
      target: result[kpi.key]?.target,
      percent: result[kpi.key]?.percent,
      players_achieved: result[kpi.key]?.achieved || 0, // Assuming achieved is for players
      teams_achieved: result[kpi.key]?.achieved_team || 0, // Assuming achieved is for teams
    }))
  }, [data, kpis, dataKeys]).filter((item) => item.metric != undefined)

  return kpi_data
}

export default function PerformancePulse({
  players,
  teams,
  onPeriodChange,
}: {
  players: number
  teams: number
  onPeriodChange?: (period_type: string, period: string) => void
}) {
  const data = usePerformanceData()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const context = useWidgetContext()

  return (
    <div
      className={clsx(classes.performance_pulse_container, {
        [classes.performance_pulse_container_mobile]: !isDesktop,
      })}
    >
      <div className={classes.performance_pulse_wrapper}>
        <Typography level={3} fs="xl" fw={500} lh="125">
          KPI Performance Pulse
        </Typography>
        <PeriodSelect
          start_month={context.competition_start || '2024-01-01'}
          end_month={context.competition_end || '2025-12-31'}
          periodType={context.period_type || 'total'}
          period={context.period || '2025-01-01'}
          onChange={(periodType, period) =>
            onPeriodChange?.(periodType, period)
          }
        />
      </div>

      <div
        className={clsx(classes.performance_container, {
          [classes.performance_container_mobile]: !isDesktop,
        })}
      >
        <div
          className={classes.pulse_grid}
          style={
            {
              '--pulse-cols': data.length,
              '--pulse-col-width': data.length > 5 ? '9rem' : '1fr',
            } as React.CSSProperties
          }
        >
          <div
            className={clsx(classes.pulse_column, classes.pulse_label_column)}
          >
            <div className={classes.pulse_cell}>
              <Typography
                fw={600}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                KPI
              </Typography>
            </div>
            <div className={classes.pulse_cell}>
              <Typography
                fw={600}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                Target
              </Typography>
            </div>
            <div className={classes.pulse_cell}>
              <Typography
                fw={600}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                Actual
              </Typography>
            </div>
            <div className={classes.pulse_cell}>
              <Typography
                fw={600}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                Teams
              </Typography>
            </div>
            <div className={clsx(classes.pulse_cell, classes.pulse_cell_last)}>
              <Typography
                fw={600}
                fc={{ light: 'black', dark: 'gray-3' }}
                lh="130"
              >
                Players
              </Typography>
            </div>
          </div>

          {data.map((item) => (
            <div
              key={item.metric.key}
              className={clsx(classes.pulse_column, classes.pulse_data_cell)}
            >
              <div className={classes.pulse_cell}>
                <StatView item={item} />
              </div>
              <div className={classes.pulse_cell}>
                <Typography
                  fc={{ light: 'gray-7', dark: 'gray-3' }}
                  lh="140"
                  ta="right"
                  className={classes.pulse_cell_wrapper}
                >
                  <Fmt
                    metric={item.metric.key}
                    component="target"
                    statistic="value"
                    value={item.target}
                  />
                </Typography>
              </div>
              <div className={classes.pulse_cell}>
                <Typography
                  fc={{ light: 'gray-7', dark: 'gray-3' }}
                  lh="140"
                  ta="right"
                  className={classes.pulse_cell_wrapper}
                >
                  <Fmt
                    metric={item.metric.key}
                    component="actual"
                    statistic="value"
                    value={item.actual}
                  />
                </Typography>
              </div>
              <div className={classes.pulse_cell}>
                <Typography
                  fc={{ light: 'primary-5', dark: 'primary-3' }}
                  ff="JetBrains Mono"
                  lh="140"
                  ta="right"
                  className={classes.pulse_cell_wrapper}
                >
                  {context.subject_type !== 'overall'
                    ? '-'
                    : `${item.teams_achieved}/${teams}`}
                </Typography>
              </div>
              <div
                className={clsx(classes.pulse_cell, classes.pulse_cell_last)}
              >
                <Typography
                  fc={{ light: 'primary-5', dark: 'primary-3' }}
                  ff="JetBrains Mono"
                  lh="140"
                  ta="right"
                  className={classes.pulse_cell_wrapper}
                >
                  {context.subject_type !== 'overall'
                    ? '-'
                    : `${item.players_achieved}/${players}`}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function StatView({ item }: { item: KPIData }) {
  const color = useFmtColor({
    metric: item.metric.key,
    component: 'percent',
    statistic: 'value',
    value: item.percent,
  })
  return (
    <StatWithIndicator
      title={item.name}
      value={item.percent ? item.percent * 100 : '-'}
      progress={100 * (item.percent || 0)}
      hideProgressBar={
        item.metric.is_percent_metric && item.metric.is_achieved_below_target
      }
      progressColor={color}
    />
  )
}
